import { useBreakpointValue } from "@chakra-ui/react/hooks";
import { VStack } from "@chakra-ui/react/stack";
import DesktopSwitchButton from "components/merchant-switch/desktop-button";
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
} from "components/ui/dialog";
import { AgentMerchantDomain } from "entities/domain/agents/agent-merchant-domain";
import {
  useCurrentAgent,
  useUnreadCounts,
} from "hooks/queries/use-current-agent";
import React, { useMemo } from "react";
import { setIsSelectorOpened } from "redux/features/merchant";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import NoAccounts from "./no-accounts";

interface SwitchAccountProps {
  merchants: AgentMerchantDomain[];
  required?: boolean;
}

const SwitchAccount = ({ required = false, merchants }: SwitchAccountProps) => {
  const dispatch = useAppDispatch();
  const { isSelectorOpened } = useAppSelector((state) => state.merchant);
  const isBaseSize = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );
  const { data: currentAgent } = useCurrentAgent();
  const { data: unreadCounts } = useUnreadCounts();

  // Use useMemo instead of useState + useEffect to avoid infinite loops
  const unreadCountByMerchant = useMemo(() => {
    if (!unreadCounts) return {};
    return unreadCounts.reduce<{ [key: number]: number }>(
      (acc, uc) => ({
        ...acc,
        [uc.merchant_id]: uc.shared,
      }),
      {}
    );
  }, [unreadCounts]);

  // Only check currentAgent - currentMerchant may not exist yet when required=true
  // (user needs to select a merchant first)
  if (!currentAgent) {
    return null;
  }

  return (
    <DialogRoot
      {...(required
        ? {
            closeOnEscape: false,
            closeOnInteractOutside: false,
          }
        : {})}
      open={isSelectorOpened}
      size={isBaseSize ? "full" : "md"}
      onOpenChange={({ open: newIsOpen }) => {
        if (newIsOpen) {
          return;
        }

        dispatch(setIsSelectorOpened(false));
      }}
    >
      <DialogContent>
        <DialogHeader textAlign="center">Select Account</DialogHeader>
        {required ? null : <DialogCloseTrigger />}
        <DialogBody>
          {merchants.length === 0 ? (
            <NoAccounts />
          ) : (
            <VStack w={isBaseSize ? "100%" : "80%"} mx="auto" mb={4}>
              {merchants
                .sort(AgentMerchantDomain.sortMerchantsAlphabetically)
                .map((merchant, index) => (
                  <DesktopSwitchButton
                    key={merchant.id}
                    merchantOption={merchant}
                    index={index}
                    size="md"
                    unreadCount={unreadCountByMerchant[merchant.id]}
                  />
                ))}
            </VStack>
          )}
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
};

export default SwitchAccount;
